import { getProjectsFromWrapper } from 'API/project-wrapper';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import useErrorStore from 'stores/error-store';
import { LinkedinSearchProject } from '../linkedin-profile-finder/linkedin-profile-finder.types';
import { LinkedinProject } from './profile-generator.types';

export enum ProjectWrapperKeys {
  CLASSIFICATION_PROJECT = 'classification-project',
  URL_FINDER_PROJECT = 'url-finder-project',
}

export const useQueryProjectWrapper = () => {
  useUserData();
  const [searchParams, setSearchParam] = useSearchParams();
  const location = useLocation();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [classificationProjectName, setClassificationProjectName] = useState('');
  const [classificationProjects, setClassificationProjects] = useState<LinkedinProject[]>([]);

  const [urlFinderProjectName, setUrlFinderProjectName] = useState('');
  const [urlFinderProjects, setUrlFinderProjects] = useState<LinkedinSearchProject[]>([]);

  const fetchClassificationProjectsFromWrapper = async () => {
    const queryClassificationProjectWrapper = searchParams.get(ProjectWrapperKeys.CLASSIFICATION_PROJECT);

    if (!_.isNil(queryClassificationProjectWrapper)) {
      const res = await getProjectsFromWrapper(queryClassificationProjectWrapper);

      if (res && !res.error) {
        const projects = res.linkedinProjects;
        const wrapper = res.projectWrapper;
        setClassificationProjects(projects);
        setClassificationProjectName(wrapper);
      } else {
        handleOpenErrorDialog(res?.error, '/projects/wrappers/:wrapperId', 'get');
      }
    }
  };

  const fetchUrlFinderProjectsFromWrapper = async () => {
    const queryUrlFinderProjectWrapper = searchParams.get(ProjectWrapperKeys.URL_FINDER_PROJECT);

    if (!_.isNil(queryUrlFinderProjectWrapper)) {
      const res = await getProjectsFromWrapper(queryUrlFinderProjectWrapper);

      if (res && !res.error) {
        const projects = res.linkedinSearchProjects;
        const wrapper = res.projectWrapper;
        setUrlFinderProjects(projects);
        setUrlFinderProjectName(wrapper);
      } else {
        handleOpenErrorDialog(res?.error, '/projects/wrappers/:wrapperId', 'get');
      }
    }
  };

  useEffect(() => {
    const queryClassificationProjectWrapper = searchParams.get(ProjectWrapperKeys.CLASSIFICATION_PROJECT);
    const queryUrlFinderProjectWrapper = searchParams.get(ProjectWrapperKeys.URL_FINDER_PROJECT);

    if (!_.isNil(queryClassificationProjectWrapper)) {
      (async () => {
        const res = await getProjectsFromWrapper(queryClassificationProjectWrapper);

        if (res && !res.error) {
          const projects = res.linkedinProjects;
          const wrapper = res.projectWrapper;
          setClassificationProjects(projects);
          setClassificationProjectName(wrapper);
        } else {
          handleOpenErrorDialog(res?.error, '/projects/wrappers/:wrapperId', 'get');
        }
      })();
    } else {
      setClassificationProjects([]);
    }

    if (!_.isNil(queryUrlFinderProjectWrapper)) {
      (async () => {
        const res = await getProjectsFromWrapper(queryUrlFinderProjectWrapper);

        if (res && !res.error) {
          const projects = res.linkedinSearchProjects;
          const wrapper = res.projectWrapper;
          setUrlFinderProjects(projects);
          setUrlFinderProjectName(wrapper);
        } else {
          handleOpenErrorDialog(res?.error, '/projects/wrappers/:wrapperId', 'get');
        }
      })();
    } else {
      setUrlFinderProjects([]);
    }
  }, [searchParams]);

  const setClassificationWrapperQuery = (wrapperId: string) => {
    setSearchParam({ [ProjectWrapperKeys.CLASSIFICATION_PROJECT]: wrapperId });
  };

  const setUrlFinderWrapperQuery = (wrapperId: string) => {
    setSearchParam({ [ProjectWrapperKeys.URL_FINDER_PROJECT]: wrapperId }, { state: location.state });
  };

  const resetQuery = () => {
    setSearchParam({}, { state: location.state });
  };

  const resetProjects = () => {
    setClassificationProjects([]);
    setUrlFinderProjects([]);
  };

  return {
    classificationProjectName,
    classificationProjects,
    fetchClassificationProjectsFromWrapper,
    setClassificationWrapperQuery,
    urlFinderProjectName,
    urlFinderProjects,
    fetchUrlFinderProjectsFromWrapper,
    setUrlFinderWrapperQuery,
    resetQuery,
    resetProjects,
  };
};
